import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API     = process.env.REACT_APP_API_BASE_URL;
const PROJECT = process.env.REACT_APP_PROJECT_NAME || 'Ariana Shipping Line LLC';

const C = {
	primary:  '#0d6efd',
	success:  '#198754',
	danger:   '#dc3545',
	border:   '#dee2e6',
	bg:       '#f0f2f5',
	card:     '#ffffff',
	text:     '#212529',
	muted:    '#6c757d',
	dark:     '#0f3460',
	darker:   '#16213e',
	darkest:  '#1a1a2e',
};

// ─── tiny reusable components ────────────────────────────────────────────────

const SectionCard = ({ icon, title, children }) => (
	<div style={{ background: C.card, borderRadius: 16, boxShadow: '0 2px 20px rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
		<div style={{ background: `linear-gradient(90deg, ${C.dark}, ${C.darker})`, padding: '13px 22px', display: 'flex', alignItems: 'center', gap: 10 }}>
			<span style={{ fontSize: 18 }}>{icon}</span>
			<span style={{ color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: 0.4 }}>{title}</span>
		</div>
		<div style={{ padding: '18px 22px' }}>{children}</div>
	</div>
);

const InfoRow = ({ label, value }) => (
	<div style={{ display: 'flex', padding: '9px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'flex-start' }}>
		<div style={{ width: 150, flexShrink: 0, color: C.muted, fontSize: 13, fontWeight: 600 }}>{label}</div>
		<div style={{ color: C.text, fontSize: 13, flex: 1 }}>{value || '—'}</div>
	</div>
);

const Skeleton = ({ h = 14, w = '100%', mb = 8 }) => (
	<div style={{ height: h, width: w, background: 'linear-gradient(90deg,#e9ecef 25%,#f8f9fa 50%,#e9ecef 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', borderRadius: 6, marginBottom: mb }} />
);

// ─── Signature pad ────────────────────────────────────────────────────────────

const SignaturePad = ({ canvasRef, onHasSign }) => {
	const drawing = useRef(false);
	const last    = useRef({ x: 0, y: 0 });

	const pos = (e, cv) => {
		const r = cv.getBoundingClientRect();
		const s = e.touches ? e.touches[0] : e;
		return {
			x: (s.clientX - r.left) * (cv.width / r.width),
			y: (s.clientY - r.top)  * (cv.height / r.height),
		};
	};

	const down = (e) => {
		e.preventDefault();
		drawing.current = true;
		last.current = pos(e, canvasRef.current);
	};

	const move = (e) => {
		e.preventDefault();
		if (!drawing.current) return;
		const cv  = canvasRef.current;
		const ctx = cv.getContext('2d');
		const p   = pos(e, cv);
		ctx.beginPath();
		ctx.moveTo(last.current.x, last.current.y);
		ctx.lineTo(p.x, p.y);
		ctx.strokeStyle = C.darkest;
		ctx.lineWidth   = 2.5;
		ctx.lineCap     = 'round';
		ctx.lineJoin    = 'round';
		ctx.stroke();
		last.current = p;
		onHasSign(true);
	};

	const up = () => { drawing.current = false; };

	const clear = () => {
		const cv = canvasRef.current;
		cv.getContext('2d').clearRect(0, 0, cv.width, cv.height);
		onHasSign(false);
	};

	return (
		<div>
			<div style={{ border: `2px dashed ${C.border}`, borderRadius: 10, background: '#fafbfc', overflow: 'hidden' }}>
				{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
				<canvas
					ref={canvasRef}
					width={620}
					height={180}
					style={{ width: '100%', height: 180, display: 'block', cursor: 'crosshair', touchAction: 'none' }}
					onMouseDown={down}
					onMouseMove={move}
					onMouseUp={up}
					onMouseLeave={up}
					onTouchStart={down}
					onTouchMove={move}
					onTouchEnd={up}
				/>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
				<span style={{ fontSize: 12, color: C.muted }}>Draw your signature above</span>
				<button
					type='button'
					onClick={clear}
					style={{ padding: '5px 14px', border: `1px solid ${C.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 12, color: C.muted }}>
					✕ Clear
				</button>
			</div>
		</div>
	);
};

// ─── Permission checkbox ──────────────────────────────────────────────────────

const PermCheck = ({ id, label, icon, checked, onChange }) => (
	<label
		htmlFor={id}
		style={{
			display: 'flex',
			alignItems: 'center',
			gap: 10,
			padding: '11px 16px',
			border: `1.5px solid ${checked ? C.primary : C.border}`,
			borderRadius: 10,
			cursor: 'pointer',
			background: checked ? '#f0f6ff' : '#fafbfc',
			transition: 'all 0.15s',
			flex: '1 1 calc(50% - 8px)',
			minWidth: 160,
		}}>
		<input
			id={id}
			type='checkbox'
			checked={checked}
			onChange={onChange}
			style={{ width: 18, height: 18, accentColor: C.primary, cursor: 'pointer', flexShrink: 0 }}
		/>
		<span style={{ fontSize: 14 }}>{icon}</span>
		<span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: checked ? C.dark : C.text }}>{label}</span>
	</label>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const RepresentativePublicPage = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');

	const [loading,     setLoading]     = useState(true);
	const [error,       setError]       = useState('');
	const [data,        setData]        = useState(null);

	// editable permissions (local state)
	const [perms, setPerms] = useState({
		pickup_vehicle: false,
		pickup_docs:    false,
		pay_money:      false,
		collect_money:  false,
	});

	const signatureCanvasRef              = useRef(null);
	const [hasSigned,    setHasSigned]    = useState(false);
	const [agreed,       setAgreed]       = useState(false);
	const [submitting,   setSubmitting]   = useState(false);
	const [submitted,    setSubmitted]    = useState(false);
	const [submitError,  setSubmitError]  = useState('');

	useEffect(() => {
		if (!token) {
			setError('No token provided. Please use the correct link.');
			setLoading(false);
			return;
		}
		axios
			.get(`${API}/public/representatives/${encodeURIComponent(token)}`)
			.then((res) => {
				if (res.data?.success) {
					setData(res.data.data);
					const r = res.data.data?.representative;
					if (r) {
						setPerms({
							pickup_vehicle: !!r.pickup_vehicle,
							pickup_docs:    !!r.pickup_docs,
							pay_money:      !!r.pay_money,
							collect_money:  !!r.collect_money,
						});
						if (r.signature) setSubmitted(true);
					}
				} else {
					setError(res.data?.message || 'Could not load representative information.');
				}
			})
			.catch((err) => {
				setError(err.response?.data?.message || 'This link is invalid or has expired.');
			})
			.finally(() => setLoading(false));
	}, [token]);

	const togglePerm = (key) =>
		setPerms((p) => ({ ...p, [key]: !p[key] }));

	const handleSubmit = useCallback(async () => {
		setSubmitError('');
		if (!hasSigned) { setSubmitError('Please draw your signature before submitting.'); return; }
		if (!agreed)    { setSubmitError('Please accept the Terms and Conditions.');        return; }

		setSubmitting(true);
		try {
			const blob = await new Promise((resolve) =>
				signatureCanvasRef.current.toBlob(resolve, 'image/png'),
			);

			const fd = new FormData();
			fd.append('signature',      blob,                              'signature.png');
			fd.append('pickup_vehicle', perms.pickup_vehicle ? '1' : '0');
			fd.append('pickup_docs',    perms.pickup_docs    ? '1' : '0');
			fd.append('pay_money',      perms.pay_money      ? '1' : '0');
			fd.append('collect_money',  perms.collect_money  ? '1' : '0');

			const res = await axios.post(
				`${API}/public/representatives/${encodeURIComponent(token)}/sign`,
				fd,
			);
			if (res.data?.success) setSubmitted(true);
			else setSubmitError(res.data?.message || 'Submission failed.');
		} catch (err) {
			setSubmitError(err.response?.data?.message || 'Submission failed. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}, [hasSigned, agreed, token, perms]);

	const rep  = data?.representative;
	const cust = data?.customer;

	return (
		/* position:fixed covers the full viewport regardless of sidebar/header layout */
		<div style={{
			position: 'fixed',
			inset: 0,
			overflowY: 'auto',
			background: C.bg,
			zIndex: 1000,
			fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
		}}>
			<style>{`
				@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
				@keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
				.pub-fade { animation: fadeUp 0.4s ease both; }
			`}</style>

			{/* ── Banner ── */}
			<div style={{ background: `linear-gradient(135deg,${C.darkest},${C.darker},${C.dark})`, padding: '36px 24px 30px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}>
				<h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 6px', letterSpacing: 0.3 }}>{PROJECT}</h1>
				<p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: 13 }}>Representative Verification &amp; Authorization</p>
			</div>

			{/* ── Content ── */}
			<div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 16px 48px' }}>

				{/* Loading */}
				{loading && (
					<div>
						{[0,1,2].map((i) => (
							<div key={i} style={{ background: C.card, borderRadius: 16, boxShadow: '0 2px 20px rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
								<div style={{ background: '#e9ecef', height: 46 }} />
								<div style={{ padding: '18px 22px' }}>
									<Skeleton h={13} w='45%' mb={12} />
									<Skeleton h={11} mb={9} />
									<Skeleton h={11} w='75%' mb={9} />
									<Skeleton h={11} w='55%' mb={0} />
								</div>
							</div>
						))}
					</div>
				)}

				{/* Error */}
				{!loading && error && (
					<div style={{ background: '#fff8f8', border: `1px solid ${C.danger}`, borderRadius: 16, padding: 36, textAlign: 'center' }}>
						<div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
						<h5 style={{ color: C.danger, marginBottom: 8 }}>Access Error</h5>
						<p style={{ color: C.muted, margin: 0 }}>{error}</p>
					</div>
				)}

				{/* Already signed / success */}
				{!loading && submitted && (
					<div className='pub-fade' style={{ background: '#f0fff8', border: `1px solid ${C.success}`, borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 4px 24px rgba(25,135,84,0.1)' }}>
						<div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
						<h4 style={{ color: C.success, marginBottom: 8, fontWeight: 700 }}>Signature Submitted</h4>
						<p style={{ color: C.muted, margin: 0 }}>
							This representative form has been signed and verified.<br />The representative Status is now <strong style={{ color: C.success }}>Active</strong>.
						</p>
					</div>
				)}

				{/* Main form */}
				{!loading && !error && !submitted && data && (
					<div className='pub-fade'>

						{/* 1 — Customer */}
						<SectionCard icon='🏢' title='Customer Information'>
							<InfoRow label='Customer ID'   value={cust?.legacy_customer_id} />
							<InfoRow label='Customer Name' value={cust?.customer_name} />
							<InfoRow label='Company'       value={cust?.company_name} />
							<InfoRow label='Phone'         value={cust?.phone} />
							<InfoRow label='Email'         value={cust?.email} />
						</SectionCard>

						{/* 2 — Representative */}
						<SectionCard icon='👤' title='Representative Information'>
							<div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 4 }}>
								{/* Photo */}
								{rep?.photo ? (
									<img
										src={rep.photo}
										alt={rep.name}
										style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${C.dark}`, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', flexShrink: 0 }}
									/>
								) : (
									<div style={{ width: 88, height: 88, borderRadius: '50%', background: `linear-gradient(135deg,${C.dark},${C.darker})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0 }}>
										👤
									</div>
								)}
								<div style={{ flex: 1, minWidth: 180 }}>
									<InfoRow label='Name'   value={rep?.name} />
									<InfoRow label='Mobile' value={rep?.mobile} />
									<InfoRow label='Email'  value={rep?.email} />
								</div>
							</div>
						</SectionCard>

						{/* 3 — Permissions (editable checkboxes) */}
						<SectionCard icon='🔑' title='Permissions'>
							<p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
								Review and confirm the permissions for this representative.
							</p>
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
								<PermCheck
									id='perm_pv'
									label='Pickup Vehicle'
									icon='🚗'
									checked={perms.pickup_vehicle}
									onChange={() => togglePerm('pickup_vehicle')}
								/>
								<PermCheck
									id='perm_pd'
									label='Pickup Docs'
									icon='📄'
									checked={perms.pickup_docs}
									onChange={() => togglePerm('pickup_docs')}
								/>
								<PermCheck
									id='perm_pm'
									label='Pay Money'
									icon='💳'
									checked={perms.pay_money}
									onChange={() => togglePerm('pay_money')}
								/>
								<PermCheck
									id='perm_cm'
									label='Collect Money'
									icon='💰'
									checked={perms.collect_money}
									onChange={() => togglePerm('collect_money')}
								/>
							</div>
						</SectionCard>

						{/* 4 — Signature */}
						<SectionCard icon='✍️' title='Digital Signature'>
							<p style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
								Sign below using your mouse or finger to authorize this representative agreement.
							</p>
							<SignaturePad canvasRef={signatureCanvasRef} onHasSign={setHasSigned} />
						</SectionCard>

						{/* 5 — Agreement + Submit */}
						<div style={{ background: C.card, borderRadius: 16, boxShadow: '0 2px 20px rgba(0,0,0,0.07)', padding: '20px 22px' }}>
							<label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 20 }}>
								<input
									type='checkbox'
									checked={agreed}
									onChange={(e) => setAgreed(e.target.checked)}
									style={{ marginTop: 2, width: 18, height: 18, accentColor: C.primary, cursor: 'pointer', flexShrink: 0 }}
								/>
								<span style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>
									I confirm that the information above is correct and I authorize this representative to act on my behalf per the permissions listed.
									I agree to the{' '}
									<span style={{ color: C.primary, textDecoration: 'underline', cursor: 'pointer' }}>
										Terms and Conditions
									</span>.
								</span>
							</label>

							{submitError && (
								<div style={{ background: '#fff5f5', border: `1px solid ${C.danger}`, borderRadius: 8, padding: '10px 14px', color: C.danger, fontSize: 13, marginBottom: 16 }}>
									⚠️ {submitError}
								</div>
							)}

							<button
								type='button'
								onClick={handleSubmit}
								disabled={submitting}
								style={{
									width: '100%',
									padding: '14px',
									background: submitting
										? C.muted
										: `linear-gradient(135deg, ${C.dark}, ${C.darkest})`,
									color: '#fff',
									border: 'none',
									borderRadius: 10,
									fontSize: 15,
									fontWeight: 700,
									cursor: submitting ? 'not-allowed' : 'pointer',
									boxShadow: submitting ? 'none' : `0 4px 18px rgba(15,52,96,0.35)`,
									letterSpacing: 0.3,
									transition: 'all 0.2s',
								}}>
								{submitting ? '⏳  Submitting…' : '✅  Submit & Verify'}
							</button>
						</div>
					</div>
				)}

				<p style={{ textAlign: 'center', color: C.muted, fontSize: 11, marginTop: 32 }}>
					© {new Date().getFullYear()} {PROJECT} — Secure authorized link
				</p>
			</div>
		</div>
	);
};

export default RepresentativePublicPage;
